import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAllAchievements } from '../utils/rankUtils';
import Modal from './Modal';
import './Achievements.css';

interface AchievementsProps {
  onNavigate: (page: string) => void;
}

const Achievements: React.FC<AchievementsProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'activity' | 'streak' | 'social' | 'special' | 'milestone' | 'location'>('all');
  const [showShareModal, setShowShareModal] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  if (!user) return null;

  const allAchievements = getAllAchievements();
  const userAchievementIds = user.achievements.map(a => a.id);

  const filteredAchievements = allAchievements.filter(achievement => 
    selectedCategory === 'all' || achievement.category === selectedCategory
  );

  const unlockedCount = user.achievements.length;
  const totalCount = allAchievements.length;
  const progress = (unlockedCount / totalCount) * 100;

  const categories = [
    { id: 'all', name: 'All', icon: '🏆' },
    { id: 'activity', name: 'Activity', icon: '🎯' },
    { id: 'milestone', name: 'Milestones', icon: '🏅' },
    { id: 'streak', name: 'Streaks', icon: '🔥' },
    { id: 'location', name: 'Location', icon: '🗺️' },
    { id: 'social', name: 'Social', icon: '❤️' },
    { id: 'special', name: 'Special', icon: '⭐' }
  ];

  const handleShare = (achievement: any) => {
    setShowShareModal(achievement.id);
  };

  const generateShareImage = () => {
    // In a real app, this would generate an actual image
    // For now, we'll just copy text to clipboard
    const text = `🎉 I just unlocked "${showShareModal}" achievement on Campus Cleanliness! Join me in keeping our campus clean! 🌱`;
    navigator.clipboard.writeText(text);
    setShowSuccessModal(true);
    setShowShareModal(null);
  };

  return (
    <div className="achievements-page">
      <div className="achievements-header">
        <button onClick={() => onNavigate('dashboard')} className="back-btn">
          ← Back
        </button>
        <h1>Achievements</h1>
        <div className="achievement-stats">
          <span>{unlockedCount}/{totalCount}</span>
        </div>
      </div>

      <div className="achievements-progress">
        <div className="progress-info">
          <h3>Collection Progress</h3>
          <p>{Math.round(progress)}% Complete</p>
        </div>
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      <div className="category-tabs">
        {categories.map(category => (
          <button
            key={category.id}
            className={`category-tab ${selectedCategory === category.id ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category.id as any)}
          >
            <span className="tab-icon">{category.icon}</span>
            <span>{category.name}</span>
          </button>
        ))}
      </div>

      <div className="achievements-grid">
        {filteredAchievements.map(achievement => {
          const isUnlocked = userAchievementIds.includes(achievement.id);
          const userAchievement = user.achievements.find(a => a.id === achievement.id);
          
          return (
            <div
              key={achievement.id}
              className={`achievement-card ${isUnlocked ? 'unlocked' : 'locked'}`}
              onClick={isUnlocked ? () => handleShare(achievement) : undefined}
            >
              <div className="achievement-icon">
                {achievement.icon}
              </div>
              <h3>{achievement.name}</h3>
              <p>{achievement.description}</p>
              {isUnlocked && userAchievement?.unlockedAt && (
                <div className="unlock-info">
                  <span className="unlock-date">
                    Unlocked {new Date(userAchievement.unlockedAt).toLocaleDateString()}
                  </span>
                  <button className="share-btn" onClick={(e) => {
                    e.stopPropagation();
                    handleShare(achievement);
                  }}>
                    Share 🔗
                  </button>
                </div>
              )}
              {!isUnlocked && (
                <div className="locked-overlay">
                  <span className="lock-icon">🔒</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="modal-overlay" onClick={() => setShowShareModal(null)}>
          <div className="share-modal" onClick={e => e.stopPropagation()}>
            <h2>Share Achievement 🎉</h2>
            <div className="share-preview">
              <div className="achievement-preview">
                <div className="preview-icon">
                  {allAchievements.find(a => a.id === showShareModal)?.icon}
                </div>
                <h3>{allAchievements.find(a => a.id === showShareModal)?.name}</h3>
                <p>Campus Cleanliness Champion</p>
              </div>
            </div>
            <div className="share-buttons">
              <button onClick={generateShareImage} className="share-option">
                📋 Copy to Clipboard
              </button>
              <button onClick={() => setShowShareModal(null)} className="cancel-share">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        type="success"
        title="Copied!"
        message="Achievement copied to clipboard! Share it on social media 🎉"
        autoCloseDelay={3000}
      />
    </div>
  );
};

export default Achievements;